import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Badge, Tooltip, Button, HStack, Icon, Text, Box } from "@chakra-ui/react";
import { FiCopy, FiTrash, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { DarwinAgentType } from "../../build-ts/common";
import { getAgentsList, duplicateAgents, deleteAgent } from "../../services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { epochConverterWithTimeZone } from "../../utils/dateUtils";
import { useUser } from "context/UserContext";
import { getAgentTypeColorForDarwinEva } from "utils/helperFuncUtils";
import ConfirmationModal from "../common/ConfirmationModal";
import { DarwinHeader, DarwinHeaderList } from "build-ts/common";


const AgentTable: React.FC = () => {
  const navigate = useNavigate();
  const hasMounted = useRef(false);
  const { defaultTimeZone } = useUser();

  // State for data
  const [agents, setAgents] = useState<DarwinHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Agent filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    type: "all",
    active: "all",
  });
  const filtersRef = useRef(filters);

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Selection state
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  // Delete confirmation modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch agents from Darwin API
  const fetchAgents = useCallback(async (
      reset = false,
      currentSortBy = "created_date",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.type && currentFilters.type !== "all") {
        params.append("agent_type", currentFilters.type);
      }
      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("name", currentFilters.search);
      }
      if (currentFilters.active && currentFilters.active !== "all") {
        params.append("is_active", currentFilters.active);
      }

      const result: DarwinHeaderList = DarwinHeaderList.fromJSON(await getAgentsList(params));

      if (result.headers) {
        // Always replace the agents list, never append
        setAgents(result.headers);

        if (result.total) {
          setPagination({
            total: result.total,
            has_more: (currentOffset + limit) < (result.total || 0),
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no agents returned
        setAgents([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchAgents(true);
      };
      loadData();
    }
  }, [fetchAgents]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchAgents(false, sortBy, sortOrder, filters, offset);
  }, [fetchAgents, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchAgents(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchAgents, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    // Also update the ref immediately
    filtersRef.current = newFilters;
  }, []);

  const handleApplyFilters = useCallback(async () => {
    // Use the ref to get the latest filter values
    await fetchAgents(true, sortBy, sortOrder, filtersRef.current, 0);
  }, [fetchAgents, sortBy, sortOrder]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", type: "all", active: "all" };
    setFilters(clearedFilters);
    filtersRef.current = clearedFilters; // Update ref immediately
    setSortBy("created_date");
    setSortOrder("asc");
    await fetchAgents(true, "created_date", "asc", clearedFilters, 0);
  }, [fetchAgents]);

  const handleSelectionChange = useCallback((newSelectedAgents: Set<string>) => {
    setSelectedAgents(newSelectedAgents);
  }, []);

  // Get agent types from DarwinAgentType enum
  const agentTypes = useMemo(() => {
    return Object.values(DarwinAgentType).filter(
      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
    ) as string[];
  }, []);

  const handleAgentRowClick = (agent: DarwinHeader) => {
    navigate(`/eva/${agent.id}`);
  };

  const handleDuplicateSelected = async () => {
    if (selectedAgents.size === 0) return;

    try {
      const agentIds = Array.from(selectedAgents);
      await duplicateAgents(agentIds);

      // Clear selection after successful duplication
      setSelectedAgents(new Set());

      // Refresh the agents list
      await fetchAgents(true, sortBy, sortOrder, filters, 0);
    } catch (error) {
      console.error("Error duplicating agents:", error);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedAgents.size !== 1) return;
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedAgents.size !== 1) return;

    try {
      setIsDeleting(true);
      const agentId = Array.from(selectedAgents)[0];
      await deleteAgent(agentId);

      // Clear selection after successful deletion
      setSelectedAgents(new Set());

      // Refresh the agents list
      await fetchAgents(true, sortBy, sortOrder, filters, 0);
    } catch (error) {
      console.error("Error deleting agent:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Table configuration
  const tableColumns: TableColumn<DarwinHeader>[] = useMemo(() => [
    {
      key: "name",
      label: "Name",
      sortable: true,
      className: "eva-name-column",
      render: (agent) => (
        <Tooltip label={agent.name} placement="top">
          <Text fontSize="sm" color="gray.700" noOfLines={2} wordBreak="break-word" mb={0} whiteSpace="normal"
          fontWeight="600">
            {agent.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "agent_type",
      label: "Type",
      className: "eva-type-column",
      render: (agent) => {
        const agentType = agent.type as keyof typeof DarwinAgentType;
        return (
          <Badge
          colorScheme={getAgentTypeColorForDarwinEva(DarwinAgentType[agentType])}
          color="black"
          size="sm"
          fontSize="xs"
          fontWeight="bold"
          >
            {agent.type || "Unknown"}
          </Badge>
        );
      },
    },
    {
      key: "is_active",
      label: "Status",
      className: "eva-status-column",
      render: (agent) => (
        <Badge
        bg={agent.is_active ? "green.100" : "red.100"}
        variant="outline"
        size="sm"
        fontSize="xs"
        fontWeight="700"
        color="black"
        >
          {agent.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "created_date",
      label: "Created At",
      sortable: true,
      className: "eva-date-column",
      render: (agent) => (
        <Text fontSize="xs" color="gray.700" mb={0}>
          {epochConverterWithTimeZone(agent.created_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "last_modified_date",
      label: "Last Modified",
      sortable: true,
      className: "eva-date-column",
      render: (agent) => (
        <Text fontSize="xs" color="gray.700" mb={0}>
          {epochConverterWithTimeZone(agent.last_modified_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const tableFilters: TableFilter[] = useMemo(() => [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search agents...",
      width: "300px",
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      options: [
        { value: "all", label: "All Agent Types" },
        ...agentTypes.map(type => ({ value: type, label: type })),
      ],
      width: "200px",
    },
    {
      key: "active",
      type: "select",
      label: "Status",
      options: [
        { value: "all", label: "All Statuses" },
        { value: "true", label: "Active" },
        { value: "false", label: "Inactive" },
      ],
      width: "150px",
    },
  ], [agentTypes]);

  const headerActions = selectedAgents.size > 0 ? (
    <HStack spacing={4}>
      <Button
        onClick={handleDuplicateSelected}
        colorScheme="blue"
        bg="blue.600"
        color="white"
        _hover={{
          bg: "blue.700",
        }}
        size="sm"
        leftIcon={<Icon as={FiCopy} />}
      >
        Duplicate
      </Button>
      {selectedAgents.size === 1 && (
        <Button
          onClick={handleDeleteSelected}
          colorScheme="red"
          bg="red.600"
          color="white"
          _hover={{
            bg: "red.700",
          }}
          size="sm"
          leftIcon={<Icon as={FiTrash} />}
        >
          Delete
        </Button>
      )}
      <Button
        onClick={() => setSelectedAgents(new Set())}
        variant="outline"
        size="sm"
        leftIcon={<Icon as={FiX} />}
      >
        Cancel
      </Button>
    </HStack>
  ) : null;

  // Get the selected agent name for the confirmation modal
  const selectedAgentName = useMemo(() => {
    if (selectedAgents.size !== 1) return "";
    const agentId = Array.from(selectedAgents)[0];
    const agent = agents.find(a => a.id === agentId);
    return agent?.name || "Unknown Agent";
  }, [selectedAgents, agents]);

  return (
    <Box height="100%" overflowY="hidden">
      <Table
        data={agents}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        selectable={true}
        selectedItems={selectedAgents}
        onSelectionChange={handleSelectionChange}
        getItemId={(agent) => agent.id || ""}
        onRowClick={handleAgentRowClick}
        headerActions={headerActions}
        emptyMessage="No agents found"
        emptySubMessage="Try adjusting your search criteria or create a new agent"
      />

      {/* Delete Agent Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Agent"
        text={`Are you sure you want to delete the agent "${selectedAgentName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default AgentTable;
